import classNames from "classnames";
import * as React from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { dispatchSignOut } from "../../../actions/common/session";
import OperatorInterface from "../../../interfaces/OperatorInterface";
import { BoronNavigator } from "../../../navigators/BoronNavigator";
import CircleIcon from "../../atoms/CircleIcon/index";
import HeaderMenuFooter from "../../atoms/HeaderMenuFooter/index";
import Icon from "../../atoms/Icon/index";
import HeaderAccountAddition from "../../molecules/HeaderAccountAddition/index";
import HeaderAccountUsername from "../../molecules/HeaderAccountUsername/index";
import HeaderPopup from "../../molecules/HeaderPopup/index";
import styles from "./styles.scss";

export enum HeaderAccountTheme {
  Main,
  White,
}
interface Props {
  currentOperator: OperatorInterface;
  operators: OperatorInterface[];
  className?: string;
  dispatchSignOut: HandleThunkActionCreator<typeof dispatchSignOut>;
  theme?: HeaderAccountTheme;
}

const HeaderAccount = (props: Props) => {
  const [listVisible, setListVisible] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    document.addEventListener("click", closeList);
    return () => {
      document.removeEventListener("click", closeList);
    };
  }, []);

  const closeList = React.useCallback((e: any) => {
    if (buttonRef.current && buttonRef.current.contains(e.target)) {
      return;
    }

    if (listRef.current && listRef.current.contains(e.target)) {
      return;
    }

    setListVisible((prev) => {
      if (prev) {
        document.removeEventListener("click", closeList);
      }
      return false;
    });
  }, []);

  const openList = React.useCallback(() => {
    setListVisible(true);
    document.addEventListener("click", closeList);
  }, [closeList]);

  const toggleListVisibility = React.useCallback(() => {
    if (listVisible) {
      setListVisible(false);
      document.removeEventListener("click", closeList);
    } else {
      openList();
    }
  }, [listVisible, closeList, openList]);

  const nameStyleClass = React.useMemo((): string => {
    switch (props.theme) {
      case HeaderAccountTheme.White:
        return `${styles.name} ${styles.white}`;
      case HeaderAccountTheme.Main:
        return `${styles.name} ${styles.main}`;
      default:
        return `${styles.name} ${styles.main}`;
    }
  }, [props.theme]);

  const isCurrentOperator = React.useCallback(
    (operator: OperatorInterface): boolean => {
      return props.currentOperator.id === operator.id;
    },
    [props.currentOperator.id],
  );

  const renderAccountUsername = React.useCallback(
    (operator: OperatorInterface) => {
      return (
        <HeaderAccountUsername
          key={`header-account-current-username-${operator.id}`}
          operator={operator}
          isCurrentOperator={isCurrentOperator(operator)}
        />
      );
    },
    [isCurrentOperator],
  );

  const renderCurrentAccountUsername = React.useCallback(() => {
    const currentOperator = props.operators.find(
      (operator: OperatorInterface) => {
        return isCurrentOperator(operator);
      },
    );

    if (!currentOperator) {
      return null;
    }

    return renderAccountUsername(currentOperator);
  }, [props.operators, isCurrentOperator, renderAccountUsername]);

  const renderOtherAccountUsernames = React.useCallback(() => {
    if (props.operators.length > 0) {
      return props.operators
        .filter((operator) => !isCurrentOperator(operator))
        .map((others) => {
          return renderAccountUsername(others);
        });
    } else {
      return null;
    }
  }, [props.operators, isCurrentOperator, renderAccountUsername]);

  const renderPopup = React.useCallback(() => {
    if (listVisible) {
      return (
        <div ref={listRef}>
          <HeaderPopup widthType="narrow">
            {renderCurrentAccountUsername()}
            {renderOtherAccountUsernames()}
            <HeaderAccountAddition onAdd={handleAddAccount} />
            <HeaderMenuFooter
              label="ログアウト"
              onClick={props.dispatchSignOut}
            />
          </HeaderPopup>
        </div>
      );
    } else {
      return null;
    }
  }, [
    listVisible,
    renderCurrentAccountUsername,
    renderOtherAccountUsernames,
    props.dispatchSignOut,
  ]);

  const attachParentClassName = React.useCallback((props: Props) => {
    if (props.className) {
      return `${styles.root} ${props.className}`;
    } else {
      return styles.root;
    }
  }, []);

  const handleAddAccount = React.useCallback((): void => {
    BoronNavigator.signInWithBackTo(location.pathname);
  }, []);

  return (
    <div className={attachParentClassName(props)}>
      <button
        onClick={toggleListVisibility}
        className={styles.button}
        ref={buttonRef}
      >
        <CircleIcon
          className={styles.iconAccountWrapper}
          iconProps={{
            name: "icon-account",
            iconClassName: classNames(styles.iconAccount, {
              [styles.white]: props.theme === HeaderAccountTheme.White,
            }),
          }}
        />
        <div className={nameStyleClass}>{props.currentOperator.fullName}</div>
        <div className={styles.arrow}>
          <Icon name="icon-arrow-drop-down" className={styles.iconArrow} />
        </div>
      </button>
      {renderPopup()}
    </div>
  );
};

const mapDispatchToProps = {
  dispatchSignOut,
};

export default connect(null, mapDispatchToProps)(HeaderAccount);
